
import {
  ComponentInternalInstance,
  computed,
  defineComponent,
  getCurrentInstance,
  ref,
  watch,
  toRefs,
  onMounted,
} from 'vue-demi';
import Popup from '@/components/popup/popup-new.vue';
import adsCreative from '@/store/modules/ads_creative';
import { SUPPORT_FILE_TYPE } from '@/store/modules/ads_creative/const';
import { ImageMaterial, VideoMaterial } from 'src/pages/creative/video-upload/material';
import { MaterialSource, MaterialStatus, MaterialType, VIDEO_STAGE } from '@/const/media';
import { state, state as creativeState } from '@/store/modules/ads_creative/state';
import { MAX_VIDEO_SIZE, MAX_IMAGE_SIZE } from '@/const/creative';
import { SelfServeTiktokPostMobile, SelfServeRetryModal } from '@ad/self-serve-sdk-vue';
import { useReselect } from 'src/pages/media/camera/hooks';
import PopupWarning from '@/components/popup-warning.vue';
import teaTracker from '@/tea-tracker';
import { TEA_EVENT } from '@/const/state/TeaNames';
import { getVideoInfo, uploadCreative } from '@/service/media';
import PopupConfirmButton from 'src/components/common/popupConfirmButton.vue';
import InfiniteLoading from 'vue-infinite-loading';
import PendingImage from './pendingImage.vue';
import PopupVideoPreview from '@/components/popup/popup-video-preview.vue';
import { EDIT_CREATIVES_EVENTS } from '@/pages/dashboard/perf/const/tea-events';
import i18n from '@/i18n';
import { isObserverOfAgent } from '@/utils/authUtil';

const name = 'video-upload';

export default defineComponent({
  name,
  components: {
    PopupVideoPreview,
    Popup,
    PopupWarning,
    PendingImage,
    InfiniteLoading,
    PopupConfirmButton,
    SelfServeTiktokPostMobile,
    SelfServeRetryModal,
  },
  props: {
    showVideoUpload: {
      type: Boolean,
      default: false,
    },
    preSelectedId: {
      type: String,
      default: '',
    },
    autoUploadMode: {
      type: Boolean,
      default: true,
    },
    distance: {
      type: Number,
      default: 2,
    },
    videoStage: {
      type: Number,
      default: 0,
    },
  },
  setup(props, { emit }) {
    const tempVideoInstance = ref({} as any);
    const showUploadError = computed(() => adsCreative.getters.general.value.videoUploadFailed);
    const selectionPopup = ref();
    const showRetryModal = ref(false);
    const { videoStage } = toRefs(props);
    const { cameraWarningMsg, showWarning, reselectWarning, hideWarning } = useReselect();
    const { proxy: _this } = getCurrentInstance() as ComponentInternalInstance;
    const videoUploadDialogShow = ref(false);
    const imageUploadDialogShow = ref(false);
    const showVideoPlayer = ref(false);
    const videoUrl = ref('');
    const selectedVideoId = ref('');
    const loadingVideo = ref(false);
    const confirmingStage = computed(
      () => videoStage.value !== VIDEO_STAGE.INIT && videoStage.value !== VIDEO_STAGE.FINISH,
    );
    const videoList = computed(() => creativeState.identity?.libraryInfo.value?.videoInfo?.list || []);
    const videoIds = computed(() =>
      creativeState.identity?.libraryInfo.value?.videoInfo?.list?.map(video => video.info.vid),
    );
    const imageLoadingStage = ref(false);
    const uploadingFile = ref(0);
    const loadingConfirm = ref(false);
    watch(videoStage, async val => {
      if (val === VIDEO_STAGE.FINISH && !loadingConfirm.value) {
        try {
          loadingVideo.value = true;
          await adsCreative.actions.getFreshLibraryList();
          selectedVideoId.value = videoIds.value[0];
          emit('update-vid', selectedVideoId.value);
        } catch (e) {
          _this.$toast(_this.$t('common_retry'));
        } finally {
          loadingVideo.value = false;
        }
      }
    });
    const ifIdInVideoList = computed(() => videoIds?.value?.includes(selectedVideoId.value));
    const imageList = computed(() => state.identity?.mediaInfo?.value?.imageInfo);
    watch(imageUploadDialogShow, () => {
      if (imageUploadDialogShow.value) {
        emit('active-image-drawer');
      }
    });
    watch(videoUploadDialogShow, () => {
      if (videoUploadDialogShow.value) {
        teaTracker.trackEvent(TEA_EVENT.USER_CHOOSE_UPLOAD_A_VIDEO);
      }
    });
    const handleCancel = (): void => {
      videoUploadDialogShow.value = false;
      emit('close');
    };
    const onScrollToBottom = async $state => {
      if (adsCreative.getters.libraryFormData.value.total < adsCreative.getters.libraryFormData.value.page * 32) {
        $state.complete();
        return;
      }
      adsCreative.mutations.setLibraryPage(adsCreative.getters.libraryFormData.value.page + 1);
      await adsCreative.actions.getLibraryList(adsCreative.getters.libraryFormData.value.page);
      $state.loaded();
    };
    const handleVideoInfo = async (vid: string) => {
      const { data } = await uploadCreative(vid);
      const { video_info } = data;
      const { video_list, poster_url, video_duration } = await getVideoInfo({ vid });
      const videosList = Object.values(video_list)
        .sort((o1, o2) => o2.bitrate - o1.bitrate)
        .map(item => item);
      const { bitrate, vheight, vwidth, size, main_url } = videosList?.[0] || {};
      return {
        name: video_info.name,
        bitrate,
        main_url,
        size,
        vid,
        width: vwidth,
        cover_url: poster_url,
        duration: video_duration,
        height: vheight,
      };
    };
    const selectAMaterial = (material): void => {
      teaTracker.trackEvent(EDIT_CREATIVES_EVENTS.ml_ad_change_video_library_choose_video_click);
      selectedVideoId.value = selectedVideoId.value === material.info.vid ? '' : material.info.vid;
    };
    const afterSuccess = () => {
      handleCancel();
      loadingConfirm.value = false;
    };

    const handleConfirm = async () => {
      teaTracker.trackEvent(EDIT_CREATIVES_EVENTS.ml_ad_change_video_library_confirm_click);
      if (!isObserverOfAgent()) {
        loadingConfirm.value = true
      }
      const material = videoList.value?.filter(video => video.info.vid === selectedVideoId.value);

      const { info } = material[0];
      // move to hooks logic
      const videoInstance = new VideoMaterial({
        source: MaterialSource.LIBRARY,
        type: MaterialType.VIDEO,
        videoInfo: {
          vid: info.vid,
          size: info.size,
          width: info.width,
          height: info.height,
          bitrate: info.bitrate,
          duration: info.duration,
          cover_url: info.cover_url || info.cover_uri,
          name: info?.name,
        },

        errorHandler: () => {
          emit('video-upload-failed');
          loadingConfirm.value = false;
        },
      });
      try {
        const videoInfo = await handleVideoInfo(info.vid);
        emit('update-instance', videoInstance, videoInfo, afterSuccess);
      } catch (e) {
        console.log('Error in uploading video: ', e);
      }
    };
    const handleImageCreateVideo = (): void => {
      const videoInstance = new VideoMaterial({
        imageList: imageList.value.map(item => item.url),
        source: MaterialSource.CAMERA_ROLL,
        type: MaterialType.IMAGE,
      });
      imageUploadDialogShow.value = false;
      imageLoadingStage.value = false;
      uploadingFile.value = 0;
      emit('update-instance-image-upload', videoInstance);
      if (videoList.value?.length > 0) {
        selectionPopup.value.$el.scrollTop = 0;
      }
      props.autoUploadMode && handleCancel();
    };
    const errorHandler = () => {
      adsCreative.mutations.setVideoUploadFailed(true);
      adsCreative.mutations.setVideoStage(0);
      loadingConfirm.value = false;
    };

    const onSelectFiles = async (file: (File & { uid: number })[]): Promise<void> => {

      const videoInstance = new VideoMaterial({
        file: file[0],
        source: MaterialSource.CAMERA_ROLL,
        type: MaterialType.VIDEO,
        errorHandler: errorHandler,
      });
      if (file[0].size > MAX_VIDEO_SIZE) {
        videoUploadDialogShow.value = false;
        reselectWarning({ warningMsg: _this.$t('module_ad_upload_popup_video_limit') });
        return;
      }
      videoUploadDialogShow.value = false;
      if (videoList.value?.length > 0) {
        selectionPopup.value.$el.scrollTop = 0;
      }
      emit('update-instance-video-upload', videoInstance);
      await videoInstance.upload();
      if (videoInstance.status === MaterialStatus.UPLOAD_FAIL) {
        tempVideoInstance.value = videoInstance;
        showRetryModal.value = true;
      }
      props.autoUploadMode && handleCancel();
    };
    const onSelectImageFiles = (uploadFilesList: (File & { uid: number })[]): void => {
      const { length } = uploadFilesList;
      const files = [] as File[];
      teaTracker.trackEvent(TEA_EVENT.USER_IN_UPLOAD_IMAGE_WINDOW);
      let exceedCount = 0;
      for (let i = 0; i < length; i++) {
        if (uploadFilesList[i].size > MAX_IMAGE_SIZE) {
          exceedCount++;
        } else {
          files.push(uploadFilesList[i]);
        }
      }
      if (exceedCount) {
        imageUploadDialogShow.value = false;
        reselectWarning({
          warningMsg:
            exceedCount === uploadFilesList.length
              ? _this.$t('module_ad_upload_popup_image_limit_all')
              : _this.$t('module_ad_upload_popup_image_limit_partial', { num: exceedCount }),
        });
        return;
      }
      imageLoadingStage.value = true;
      uploadingFile.value = files.length;
      setTimeout(() => {
        files.forEach(async (file, index) => {
          const imageInstance = new ImageMaterial({
            file,
            source: MaterialSource.CAMERA_ROLL,
          });
          await imageInstance.upload(true);
          emit('uploaded-image', imageInstance);
          uploadingFile.value = uploadingFile.value - 1;
          if (index === length - 1) {
            imageLoadingStage.value = false;
            uploadingFile.value = 0;
          }
        });
      }, 0);
    };
    const handleVideoPlay = async vid => {
      teaTracker.trackEvent(EDIT_CREATIVES_EVENTS.ml_ad_change_video_library_video_play_click);
      videoUrl.value = '';
      showVideoPlayer.value = true;
      const { video_list } = await getVideoInfo({ vid });
      const video = Object.values(video_list)[0];
      videoUrl.value = atob(video.main_url);
      console.log(atob(video.main_url));
    };
    const hancleClick = () => {
      console.log('测试 hancleClick');
    };
    onMounted(async () => {
      loadingVideo.value = true;
      teaTracker.trackEvent(TEA_EVENT.DISPLAY_VIDEO_LIBRARY);
      teaTracker.trackEvent(EDIT_CREATIVES_EVENTS.ml_ad_change_video_library_show);
      const { page } = adsCreative.getters.libraryFormData.value;
      try {
        await adsCreative.actions.getLibraryList(page);
      } catch (e) {
        console.log(e);
      } finally {
        loadingVideo.value = false;
      }
    });
    const cancelHandler = () => {
      showRetryModal.value = false;
    };
    const retryHandler = async () => {
      showRetryModal.value = false;
      await tempVideoInstance.upload();
    };

    const closeUploadModal = () => {
      imageUploadDialogShow.value = false;
      imageLoadingStage.value = false;
      uploadingFile.value = 0;
    };

    const showVideoUploadDialog = () => {
      teaTracker.trackEvent(EDIT_CREATIVES_EVENTS.ml_ad_change_video_library_upload_click);
      videoUploadDialogShow.value = true;
    };

    const showImageUploadDialog = () => {
      teaTracker.trackEvent(EDIT_CREATIVES_EVENTS.ml_ad_change_video_library_create_click);
      imageUploadDialogShow.value = true;
    };

    return {
      showUploadError,
      showImageUploadDialog,
      showVideoUploadDialog,
      selectionPopup,
      showRetryModal,
      closeUploadModal,
      cancelHandler,
      retryHandler,
      hancleClick,
      showVideoPlayer,
      videoUrl,
      handleVideoPlay,
      imageLoadingStage,
      uploadingFile,
      ifIdInVideoList,
      handleConfirm,
      cameraWarningMsg,
      showWarning,
      hideWarning,
      imageList,
      videoList,
      loadingVideo,
      selectedVideoId,
      onScrollToBottom,
      confirmingStage,
      SupportFileType: SUPPORT_FILE_TYPE,
      handleCancel,
      onSelectFiles,
      selectAMaterial,
      onSelectImageFiles,
      videoUploadDialogShow,
      imageUploadDialogShow,
      // onAppend,
      handleImageCreateVideo,
      loadingConfirm,
			isObserverOfAgent,
    };
  },
});
